import { z } from "zod";
import {
  AutoTransactionRuleDtoSchema,
  PreviewEntitiesResponseDtoSchema,
  PreviewMatchesResponseDtoSchema,
  type AutoTransactionRuleDto,
  type ConditionDto,
  type NewAutoTransactionRuleDto,
  type PreviewEntitiesResponseDto,
  type PreviewMatchesResponseDto,
  type UpdateAutoTransactionRuleDto,
} from "./types";
import { API_BASE, handleResponse, parseResponse } from "./common";
import { fetchWithInterceptor } from "./client";
import { previewableCondition } from "../logic/auto-transaction-preview";

export const getAutoTransactionRules = async (): Promise<
  AutoTransactionRuleDto[]
> => {
  const res = await fetchWithInterceptor(
    `${API_BASE}/settings/auto-transactions`,
  );
  await handleResponse(res);
  return parseResponse(
    z.array(AutoTransactionRuleDtoSchema),
    await res.json(),
    "GET /settings/auto-transactions",
  );
};

export const getAutoTransactionRule = async (
  id: string,
): Promise<AutoTransactionRuleDto> => {
  const res = await fetchWithInterceptor(
    `${API_BASE}/settings/auto-transactions/${id}`,
  );
  await handleResponse(res);
  return parseResponse(
    AutoTransactionRuleDtoSchema,
    await res.json(),
    "GET /settings/auto-transactions/:id",
  );
};

export const createAutoTransactionRule = async (
  rule: NewAutoTransactionRuleDto,
): Promise<AutoTransactionRuleDto> => {
  const res = await fetchWithInterceptor(
    `${API_BASE}/settings/auto-transactions`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(rule),
    },
  );
  await handleResponse(res);
  return parseResponse(
    AutoTransactionRuleDtoSchema,
    await res.json(),
    "POST /settings/auto-transactions",
  );
};

export const updateAutoTransactionRule = async (
  id: string,
  rule: UpdateAutoTransactionRuleDto,
): Promise<AutoTransactionRuleDto> => {
  const res = await fetchWithInterceptor(
    `${API_BASE}/settings/auto-transactions/${id}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(rule),
    },
  );
  await handleResponse(res);
  return parseResponse(
    AutoTransactionRuleDtoSchema,
    await res.json(),
    "PUT /settings/auto-transactions/:id",
  );
};

export const deleteAutoTransactionRule = async (id: string): Promise<void> => {
  const res = await fetchWithInterceptor(
    `${API_BASE}/settings/auto-transactions/${id}`,
    {
      method: "DELETE",
    },
  );
  await handleResponse(res);
};

/**
 * Asks the backend which transactions would match the given (uncommitted)
 * condition. Used by the rule-editor preview so the user can verify a rule
 * before saving. Backend caps results at 200; `truncated` flags when more
 * matches may exist beyond what was returned.
 */
export const previewAutoTransactionMatches = async (
  condition: ConditionDto,
): Promise<PreviewMatchesResponseDto> => {
  const res = await fetchWithInterceptor(
    `${API_BASE}/settings/auto-transactions/preview-matches`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ condition: previewableCondition(condition) }),
    },
  );
  await handleResponse(res);
  return parseResponse(
    PreviewMatchesResponseDtoSchema,
    await res.json(),
    "POST /settings/auto-transactions/preview-matches",
  );
};

/**
 * Asks the backend which registry entries each entity-backed leaf of an
 * (uncommitted) condition currently names — "3 sources match", or none at all.
 *
 * The answer comes from the server on purpose: it is computed by the same
 * matcher the rule engine uses, so the editor can never show a hit count the
 * engine would disagree with. Leaves are keyed by their path in the posted
 * tree; a leaf that couldn't be evaluated carries `error` instead of hits.
 */
export const previewAutoTransactionEntities = async (
  condition: ConditionDto,
): Promise<PreviewEntitiesResponseDto> => {
  const res = await fetchWithInterceptor(
    `${API_BASE}/settings/auto-transactions/preview-entities`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ condition: previewableCondition(condition) }),
    },
  );
  await handleResponse(res);
  return parseResponse(
    PreviewEntitiesResponseDtoSchema,
    await res.json(),
    "POST /settings/auto-transactions/preview-entities",
  );
};
